/** Default patrol stop duration (seconds) for a camera location. */
export const DEFAULT_LOCATION_DURATION = "2";

/**
 * @typedef {{ xPercent: number, yPercent: number, duration: string }} PatrolCameraLocation
 * @typedef {{ cameraId: string, placementId?: string, label?: string, sortOrder?: number, locations: PatrolCameraLocation[] }} PatrolCameraPlacement
 * @typedef {{ id: string, name: string, site: string, type: string }} PatrolCameraOption
 */

/**
 * Map Administration → Camera management row to patrol placement dropdown option.
 * @param {Record<string, unknown>} row
 * @returns {PatrolCameraOption | null}
 */
export function patrolCameraOptionFromAdminRow(row) {
  if (!row || typeof row !== "object") return null;
  const id = String(row.id ?? row.camera_id ?? "").trim();
  if (!id) return null;

  const name = String(row.name ?? "Camera").trim() || "Camera";
  const site = String(
    row.site ?? row.site_name ?? row.siteName ?? row.location ?? "",
  ).trim();
  const stream = String(
    row.cam360_rtsp ??
      row.cam360Rtsp ??
      row.streaming_url ??
      row.stream_url ??
      "",
  ).trim();
  const type = /rtmp/i.test(stream) ? "RTMP" : stream ? "RTSP" : "—";

  return { id, name, site, type };
}

/**
 * @param {MouseEvent} e
 * @param {HTMLElement} target
 * @returns {{ xPercent: number, yPercent: number }}
 */
export function clickToLocationPercent(e, target) {
  const img =
    target?.querySelector?.("img") ||
    (target?.tagName === "IMG" ? target : null);
  const rect = img
    ? img.getBoundingClientRect()
    : target.getBoundingClientRect();
  const xPercent = Math.min(
    1,
    Math.max(0, (e.clientX - rect.left) / rect.width),
  );
  const yPercent = Math.min(
    1,
    Math.max(0, (e.clientY - rect.top) / rect.height),
  );
  return { xPercent, yPercent };
}

/**
 * @param {unknown} loc
 * @returns {PatrolCameraLocation}
 */
export function normalizeLocation(loc) {
  const raw = loc || {};
  let xPercent = Number(raw.xPercent);
  let yPercent = Number(raw.yPercent);

  if (!Number.isFinite(xPercent) && Number.isFinite(Number(raw.x))) {
    xPercent = Number(raw.x) / 100;
  }
  if (!Number.isFinite(yPercent) && Number.isFinite(Number(raw.y))) {
    yPercent = Number(raw.y) / 100;
  }

  return {
    xPercent: Math.min(1, Math.max(0, xPercent || 0)),
    yPercent: Math.min(1, Math.max(0, yPercent || 0)),
    duration: String(raw.duration ?? DEFAULT_LOCATION_DURATION),
  };
}

/**
 * @param {PatrolCameraPlacement[]} placements
 * @returns {PatrolCameraPlacement[]}
 */
/** One camera → at most one point on the drawing. */
function oneLocationPerCamera(p) {
  const locations = (p.locations || []).map(normalizeLocation);
  const sortRaw = p.sortOrder ?? p.sort_order;
  const sortOrder =
    sortRaw !== undefined && sortRaw !== null && sortRaw !== ""
      ? Number(sortRaw)
      : undefined;
  return {
    cameraId: p.cameraId,
    placementId: p.placementId ? String(p.placementId) : undefined,
    label: p.label || "",
    sortOrder: Number.isFinite(sortOrder) ? sortOrder : undefined,
    locations: locations.length ? [locations[0]] : [],
  };
}

function mapToPlacements(placements) {
  if (!Array.isArray(placements)) return [];
  return placements
    .filter((p) => p?.cameraId)
    .map((p) => oneLocationPerCamera(p));
}

/** Patrol route order (0-based), from saved sort_order or list position. */
export function sortPlacementsByOrder(placements) {
  const list = mapToPlacements(placements);
  return [...list].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

export function normalizePlacements(placements) {
  return sortPlacementsByOrder(placements);
}

/**
 * Resolve display name from Administration camera options.
 * @param {string} cameraId
 * @param {Array<{ id: string, name: string }>} [cameras]
 */
export function cameraNameFromOptions(cameraId, cameras = []) {
  if (!cameraId) return "";
  const id = String(cameraId);
  const found = cameras.find(
    (c) => String(c?.id ?? "") === id || String(c?.id ?? "") === String(cameraId),
  );
  return String(found?.name ?? "").trim();
}

/**
 * Label for UI lists and map pins (saved label, else camera name, else id).
 * @param {PatrolCameraPlacement} placement
 * @param {Array<{ id: string, name: string }>} [cameras]
 */
export function placementDisplayLabel(placement, cameras = []) {
  if (!placement) return "";
  const saved = String(placement.label ?? "").trim();
  if (saved) return saved;
  return (
    cameraNameFromOptions(placement.cameraId, cameras) ||
    String(placement.cameraId ?? "")
  );
}

/**
 * Fill missing placement labels from the camera catalog (API often omits label).
 * @param {PatrolCameraPlacement[]} placements
 * @param {Array<{ id: string, name: string }>} [cameras]
 */
export function enrichPlacementsWithCameraLabels(placements, cameras = []) {
  return normalizePlacements(placements).map((p) => {
    const saved = String(p.label ?? "").trim();
    const resolved = saved || cameraNameFromOptions(p.cameraId, cameras);
    return { ...p, label: resolved };
  });
}

/**
 * @param {{ cameraPlacements?: PatrolCameraPlacement[], markers?: Array }} drawing
 * @returns {PatrolCameraPlacement[]}
 */
export function getDrawingCameraPlacements(drawing) {
  if (!drawing) return [];
  if (Array.isArray(drawing.cameraPlacements) && drawing.cameraPlacements.length) {
    return normalizePlacements(drawing.cameraPlacements);
  }
  if (!Array.isArray(drawing.markers)) return [];
  const byCamera = new Map();
  drawing.markers.forEach((m, i) => {
    const cameraId = m.cameraId || `legacy-${i}`;
    const loc = normalizeLocation(m);
    byCamera.set(cameraId, {
      cameraId,
      label: m.label || byCamera.get(cameraId)?.label || "",
      locations: [loc],
    });
  });
  return Array.from(byCamera.values());
}

/**
 * Flat pins for map rendering (one pin per location).
 * @param {PatrolCameraPlacement[]} placements
 * @param {Array<{ id: string, name: string }>} [cameras]
 */
export function markersForDisplay(placements, cameras = []) {
  const pins = [];
  normalizePlacements(placements).forEach((p) => {
    p.locations.forEach((loc, idx) => {
      pins.push({
        cameraId: p.cameraId,
        locationIndex: idx,
        label: placementDisplayLabel(p, cameras),
        xPercent: loc.xPercent,
        yPercent: loc.yPercent,
        duration: loc.duration,
      });
    });
  });
  return pins;
}

/**
 * @param {PatrolCameraPlacement[]} placements
 */
export function countPlacementLocations(placements) {
  return normalizePlacements(placements).filter((p) => p.locations.length > 0)
    .length;
}

/**
 * API body for one camera on a drawing.
 * @param {string} drawingId
 * @param {string} cameraId
 * @param {PatrolCameraLocation[]} locations
 */
export function buildCameraLocationSavePayload(drawingId, cameraId, locations) {
  return {
    drawingId,
    cameraId,
    locations: (locations || []).map(normalizeLocation),
  };
}

/**
 * One payload per camera (for POST/PUT per camera).
 * @param {string} drawingId
 * @param {PatrolCameraPlacement[]} placements
 */
export function buildAllCameraLocationSavePayloads(drawingId, placements) {
  return normalizePlacements(placements).map((p) =>
    buildCameraLocationSavePayload(drawingId, p.cameraId, p.locations),
  );
}

/**
 * POST /api/patrol-section-cameras/bulk request body.
 * @param {string} drawingId
 * @param {PatrolCameraPlacement[]} placements
 */
export function buildBulkCameraPlacementsBody(drawingId, placements) {
  return {
    drawingId,
    placements: normalizePlacements(placements)
      .filter((p) => p.locations.length > 0)
      .map((p, index) => ({
        cameraId: p.cameraId,
        sortOrder: Number.isFinite(p.sortOrder) ? p.sortOrder : index,
        locations: p.locations.map(normalizeLocation),
      })),
  };
}

function unwrapSectionCameraRows(body) {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== "object") return [];
  const o = body;
  if (Array.isArray(o.data)) return o.data;
  if (Array.isArray(o.placements)) return o.placements;
  if (Array.isArray(o.items)) return o.items;
  if (Array.isArray(o.cameras)) return o.cameras;
  return [];
}

/**
 * Map GET /api/patrol-section-cameras?drawingId=… → PatrolCameraPlacement[].
 * @param {unknown} body
 * @returns {PatrolCameraPlacement[]}
 */
function rowToPlacement(raw) {
  if (!raw || typeof raw !== "object") return null;
  const row = raw;
  const cameraId = row.cameraId ?? row.camera_id;
  if (!cameraId) return null;

  const label = row.label ?? row.cameraName ?? row.camera_name ?? "";
  const placementId = row.id ?? row.placement_id ?? row.placementId;
  const sortRaw = row.sortOrder ?? row.sort_order;
  const sortOrder =
    sortRaw !== undefined && sortRaw !== null && sortRaw !== ""
      ? Number(sortRaw)
      : undefined;

  let locations = [];
  if (Array.isArray(row.locations) && row.locations.length) {
    locations = row.locations.map((loc) => normalizeLocation(loc));
  } else {
    locations = [
      normalizeLocation({
        xPercent: row.xPercent ?? row.x_percent,
        yPercent: row.yPercent ?? row.y_percent,
        x: row.x ?? row.x_position ?? row.xPosition,
        y: row.y ?? row.y_position ?? row.yPosition,
        duration: row.duration,
      }),
    ];
  }

  return oneLocationPerCamera({
    cameraId: String(cameraId),
    placementId: placementId ? String(placementId) : undefined,
    label: String(label),
    sortOrder,
    locations,
  });
}

export function mapSectionCamerasFromApi(body) {
  const rows = unwrapSectionCameraRows(body);
  const sortedRows = [...rows].sort((a, b) => {
    const ao = Number(a?.sortOrder ?? a?.sort_order ?? NaN);
    const bo = Number(b?.sortOrder ?? b?.sort_order ?? NaN);
    if (Number.isFinite(ao) && Number.isFinite(bo) && ao !== bo) {
      return ao - bo;
    }
    const at = new Date(a?.createdAt ?? a?.created_at ?? 0).getTime();
    const bt = new Date(b?.createdAt ?? b?.created_at ?? 0).getTime();
    return at - bt;
  });

  return normalizePlacements(
    sortedRows.map(rowToPlacement).filter(Boolean),
  );
}
